import React from 'react';
import { formatCount } from '../../callStatusSummary.utils';

import stylesModule from './ProgressBreakdown.module.scss';

export type ProgressLegendItem = {
    id: string;
    label: string;
    count: number;
    percent: number;
    color: string;
};

export type ProgressBreakdownProps = {
    title: string;
    subtitle: string;
    items: ProgressLegendItem[];
    testId: string;
    panelId?: string;
    labelledBy?: string;
    role?: string;
    activeItemId?: string | null;
    isDimmed?: boolean;
    onItemToggle?: (item: ProgressLegendItem) => void;
    itemTestIdPrefix?: string;
    styles?: React.CSSProperties;
};

export const ProgressBreakdown: React.FC<ProgressBreakdownProps> = ({
    title,
    subtitle,
    items,
    testId,
    panelId,
    labelledBy,
    role,
    activeItemId = null,
    isDimmed = false,
    onItemToggle,
    itemTestIdPrefix = 'disposition-row',
    styles,
}) => {
    const visibleSegments = items.filter((item) => item.count > 0);
    const segments = visibleSegments.length > 0 ? visibleSegments : items;

    return (
        <div
            className={[stylesModule.deepDive, isDimmed ? stylesModule.dimmed : ''].filter(Boolean).join(' ')}
            role={role}
            id={panelId}
            aria-labelledby={labelledBy}
            data-testid={testId}
            style={styles}
        >
            <header className={stylesModule.deepDiveHeader}>
                <h4 className={stylesModule.deepDiveTitle}>{title}</h4>
                <p className={stylesModule.deepDiveSubtitle}>{subtitle}</p>
            </header>

            {items.length === 0 ? (
                <p className={stylesModule.emptyState}>No data for the current filters.</p>
            ) : (
                <>
                    <div
                        className={stylesModule.progressTrack}
                        role='img'
                        aria-label={segments.map((item) => `${item.label} ${item.percent}%`).join(', ')}
                    >
                        {segments.map((item) => {
                            const isItemDimmed = Boolean(activeItemId && activeItemId !== item.id);
                            return (
                                <div
                                    key={item.id}
                                    className={[stylesModule.progressSegment, isItemDimmed ? stylesModule.dimmed : '']
                                        .filter(Boolean)
                                        .join(' ')}
                                    style={{
                                        width: `${item.percent}%`,
                                        backgroundColor: item.color,
                                    }}
                                    title={`${item.label}: ${formatCount(item.count)} (${item.percent}%)`}
                                />
                            );
                        })}
                    </div>

                    <ul className={stylesModule.legend}>
                        {items.map((item) => {
                            const isActive = activeItemId === item.id;
                            const isItemDimmed = Boolean(activeItemId && activeItemId !== item.id);
                            const content = (
                                <>
                                    <span
                                        className={stylesModule.legendSwatch}
                                        style={{ backgroundColor: item.color }}
                                        aria-hidden='true'
                                    />
                                    <span className={stylesModule.legendLabel}>{item.label}</span>
                                    <span className={stylesModule.legendCount}>{formatCount(item.count)}</span>
                                    <span className={stylesModule.legendPercent}>{item.percent}%</span>
                                </>
                            );

                            if (!onItemToggle) {
                                return (
                                    <li key={item.id} className={stylesModule.legendItem}>
                                        {content}
                                    </li>
                                );
                            }

                            return (
                                <li key={item.id}>
                                    <button
                                        type='button'
                                        className={[
                                            stylesModule.legendItem,
                                            stylesModule.legendButton,
                                            isActive ? stylesModule.legendButtonActive : '',
                                            isItemDimmed ? stylesModule.dimmed : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onClick={() => onItemToggle(item)}
                                        aria-pressed={isActive}
                                        data-testid={`${itemTestIdPrefix}-${item.id}`}
                                    >
                                        {content}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </div>
    );
};
